import type {ModalWindowType} from '../../modals/modal-conversation.service';
import type {ActionResponse} from 'typings/portaro.be.types';
import type {ModalWindowResult} from 'typings/portaro.fe.types';
import type ModalConversationService from '../../modals/modal-conversation.service';
import type {ToastMessageService} from 'shared/components/kp-toast-messages/toast-message.service';
import {responseTypes} from '../constants/portaro.constants';
import {isInterruptedActionResponse} from '../../modals/modal-utils';
import {isActionResponse, isActionResponseWithText, isModalWindowResult} from '../utils/types-utils';
import {getErrorMessage} from '../utils/data-service-utils';
import bind from 'bind-decorator';

/**
 * @ngdoc service
 * @name finishedResponseInteractionService
 * @module portaro.services
 *
 * @required toastMessageService
 * @required modalConversationService
 *
 * @description
 * Service encapsulates logic of all options how to display finishedResponses to user
 * Responses can be in form of  ActionResponse, ModalWindowResult or HttpResponse<ActionResponse>
 * Service can display toasts or modal windows
 * there are 2 kinds of methods: for success responses called from then(), and for error responses, called from catch()
 */
export default class FinishedResponseInteractionService {
    public static readonly serviceName = 'finishedResponseInteractionService';
    private readonly ignoredResponseTypes = [responseTypes.INTERRUPT, responseTypes.ACKNOWLEDGE];
    private readonly forceModalResponseTypes = [responseTypes.REDIRECTION, responseTypes.PRINT_PAGE, responseTypes.RICH_TEXT_FINISHED_RESPONSE];

    /*@ngInject*/
    constructor(private toastMessageService: ToastMessageService, private modalConversationService: ModalConversationService) {}

    public async getResultOrFailedModalIgnoringManualInterruption<RESP>(callback: () => Promise<RESP>): Promise<RESP> {
        try {
            return await callback();
        } catch (actionResponse) {
            if (!isActionResponseWithText(actionResponse) || !isInterruptedActionResponse(actionResponse)) {
                await this.showFailedResponseInModalWindow(actionResponse);
            }
            throw actionResponse;
        }
    }

    public async showSuccessToastOrFailedModalIgnoringManualInterruption<RESP extends ActionResponse>(callback: () => Promise<RESP>): Promise<RESP> {
        return this.getResultOrFailedModalIgnoringManualInterruption(async () => {
            const response = await callback();
            await this.showSuccessResponseInToastIfPossible(response);
            return response;
        });
    }

    @bind
    public showSuccessResponseInToastIfPossible(actionResponse: ActionResponse): Promise<any> {
        if (this.isForceModal(actionResponse.responseType)) {
            return this.showSuccessResponseInModalWindow(actionResponse);
        }
        if (isActionResponseWithText(actionResponse) && !this.isIgnored(actionResponse.responseType)) {
            this.toastMessageService.showSuccess(actionResponse.text);
        }
        return Promise.resolve();
    }

    public showSuccessResponseInModalWindow(result: ActionResponse) {
        return this.showModalWindow(result, false);
    }

    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    public showFailedResponseInToast(result: ActionResponse | any) {
        if (isActionResponse(result)) {
            return this.showErrorToast(result);
        }
        return this.showNonStandardError(result);
    }

    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    public showFailedResponseInModalWindow(result: ActionResponse | any) {
        if (isActionResponse(result)) {
            return this.showModalWindow(result, true);
        }
        this.showNonStandardError(result);
        throw result;
    }

    public showSuccessModalResultInModalWindow(result: ModalWindowResult) {
        return this.showSuccessResponseInModalWindow(result.finishedResponse);
    }

    /**
     * @ngdoc method
     * @name finishedResponseInteractionService#showSuccessInModalWindow
     *
     * @param {any} result Some type of response, without explicit type
     *
     * @returns {Promise<ModalWindowResult>} promise resolved after modal window is closed
     *
     * @description
     * Takes response and displays modal window  if it is accepted type of response
     * (ActionResponse, ModalWindowResult or data object containing those)
     * This is preferred method to call from JS code.
     *
     * @deprecated - use concrete method (showSuccessResponseInModalWindow or showSuccessModalResultInModalWindow)
     */
    public showSuccessInModalWindow(result): Promise<ModalWindowResult> {
        if (isActionResponse(result)) {
            return this.showSuccessResponseInModalWindow(result);
        }
        if (isModalWindowResult(result)) {
            return this.showSuccessModalResultInModalWindow(result);
        }
        if (result.data) {
            return this.showSuccessInModalWindow(result.data);
        }
        throw new TypeError(`Can not open modal window with ${result}`);
    }

    /**
     * @ngdoc method
     * @name finishedResponseInteractionService#showFailedInToast
     *
     * @param {any} result Some type of response, without explicit type
     *
     * @returns {void}
     *
     * @description
     * Takes response and displays toast if it is accepted type of response
     * (ActionResponse, ModalWindowResult or data object containing those)
     * This is preferred method to call from JS code.
     *
     * @deprecated - use concrete method (showFailedResponseInToast or showFailedModalResultInToast or other variant)
     */
    public showFailedInToast(result: any): void {
        if (isActionResponse(result)) {
            return this.showErrorToast(result);
        }
        if (isModalWindowResult(result)) {
            return this.showErrorToast(result.finishedResponse);
        }
        if (result.data) {
            return this.showFailedInToast(result.data);
        }
        return this.showNonStandardError(result);
    }

    /**
     * @ngdoc method
     * @name finishedResponseInteractionService#showFailedInModalWindow
     *
     * @param {any} result Some type of response, without explicit type
     *
     * @returns {Promise<ModalWindowResult>} promise resolved after modal window is closed
     *
     * @description
     * Takes response and displays modal window  if it is accepted type of response
     * (ActionResponse, ModalWindowResult or data object containing those)
     * This is preferred method to call from JS code.
     */
    public showFailedInModalWindow(result: any): Promise<ModalWindowResult> {
        if (isActionResponse(result)) {
            return this.showModalWindow(result, true);
        }
        if (isModalWindowResult(result)) {
            return this.showModalWindow(result.finishedResponse, true);
        }
        if (result.data) {
            return this.showFailedInModalWindow(result.data);
        }
        this.showNonStandardError(result);
        throw result;
    }

    private showErrorToast(actionResponse: ActionResponse) {
        if (isActionResponseWithText(actionResponse) && !this.isIgnored(actionResponse.responseType)) {
            this.toastMessageService.showError(actionResponse.text);
        }
    }

    private showNonStandardError(error: any) {
        this.toastMessageService.showError(getErrorMessage(error));
    }

    private showModalWindow(actionResponse: ActionResponse, isFailed: boolean) {
        if (!this.isIgnored(actionResponse.responseType)) {
            return this.modalConversationService.openModalWindow(
                actionResponse.responseType as ModalWindowType,
                actionResponse,
                this.isComponent(actionResponse.responseType) ? {isComponent: true} : null);
        }
        if (isFailed) {
            return Promise.reject({
                finishedResponse: actionResponse,
                isSuccessful: false,
                reopen: false
            });
        }
        return Promise.resolve({
            finishedResponse: actionResponse,
            isSuccessful: true,
            reopen: false
        });
    }

    private isIgnored(responseType: string): boolean {
        return this.ignoredResponseTypes.includes(responseType);
    }

    private isForceModal(responseType: string): boolean {
        return this.forceModalResponseTypes.includes(responseType);
    }

    private isComponent(responseType: string): boolean {
        return /^[a-z][a-zA-Z0-9]+$/.test(responseType); // lower camelCase test
    }
}
